import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import AdminNav from './AdminNav';
import { useAdmin } from '../context/AdminContext';
import { LineChart } from './AnalyticsChart';

// Order = the journey, top to bottom. `source` tells the reader where each number comes from.
// Ryzz has no card trial: installing the bot starts the Free plan, and that Free period is the "trial".
// `hideIfZero` rows are Stripe card trials, shown only if someone ever creates one in Stripe,
// and Google Search rows, shown only once Search Console has data.
// `avg` rows are daily averages, not counts: null on days without data, never summed. Their Total is the API's
// average of the days with data (Google position: weighted by impressions). Shown only when some day has data.
const METRICS = [
  { key: 'searchImpressions', label: 'Google impressions', source: 'Google Search Console, times the site showed in Google results', hideIfZero: true },
  { key: 'searchClicks', label: 'Google clicks', source: 'Google Search Console, clicks from Google results to the site', hideIfZero: true },
  { key: 'searchPosition', label: 'Google avg position', source: 'Google Search Console, weighted by impressions (1 = top result)', avg: true, unit: 'position' },
  { key: 'visitors', label: 'Website visitors', source: 'Google Analytics' },
  { key: 'installStarts', label: 'Add to Discord clicks', source: 'Left the site for Discord to install (server or personal)' },
  { key: 'oauthFailures', label: 'Discord canceled / failed', source: 'Came back from Discord without finishing' },
  { key: 'registrations', label: 'Registrations', source: 'Discord login, first time' },
  { key: 'installs', label: 'Installs (Free starts)', source: 'Bot added to a server, Free plan begins' },
  { key: 'activations', label: 'First translation', source: 'First translated message in a server' },
  { key: 'checkoutsStarted', label: 'Checkouts opened', source: 'Stripe checkout page opened' },
  { key: 'checkoutsAbandoned', label: 'Checkouts abandoned', source: 'Stripe checkout expired without paying' },
  { key: 'newPaid', label: 'Free → Paid', source: 'Stripe, first payment' },
  { key: 'winBacks', label: 'Win-backs', source: 'Stripe, paid again after churn' },
  { key: 'churned', label: 'Churned', source: 'Stripe, paid subscription ended' },
  { key: 'cancelRequests', label: 'Cancel requests', source: 'Stripe, scheduled to end' },
  { key: 'uninstalls', label: 'Uninstalls', source: 'Bot removed from a server' },
  { key: 'trialsStarted', label: 'Card trials started', source: 'Stripe trial', hideIfZero: true },
  { key: 'trialConversions', label: 'Card trial → paid', source: 'Stripe trial', hideIfZero: true },
  { key: 'trialsExpired', label: 'Card trials expired', source: 'Stripe trial', hideIfZero: true },
  { key: 'revenue', label: 'Revenue', source: 'Stripe, paid invoices', money: true },
  { key: 'uxSessions', label: 'Clarity sessions', source: 'Microsoft Clarity, recorded website sessions', avg: true, unit: 'count' },
  { key: 'uxRageClickPct', label: 'Rage-click sessions %', source: 'Microsoft Clarity, sessions with repeated clicks in one spot', avg: true, unit: 'pct' },
  { key: 'uxDeadClickPct', label: 'Dead-click sessions %', source: 'Microsoft Clarity, sessions with clicks that did nothing', avg: true, unit: 'pct' },
  { key: 'uxQuickbackPct', label: 'Quickback %', source: 'Microsoft Clarity, sessions that left a page and came straight back', avg: true, unit: 'pct' },
  { key: 'uxScrollDepth', label: 'Avg scroll depth %', source: 'Microsoft Clarity, how far down the page visitors scroll', avg: true, unit: 'pct' },
  { key: 'uxActiveTimeSec', label: 'Avg active time', source: 'Microsoft Clarity, time spent actively using the site', avg: true, unit: 'time' },
];

const SEARCH_STEPS = ['searchImpressions', 'searchClicks'];
const FUNNEL = [...SEARCH_STEPS, 'visitors', 'installStarts', 'installs', 'activations', 'checkoutsStarted', 'newPaid'];
const UX_TILES = [
  ['uxSessions', 'Sessions', 'Per day'],
  ['uxRageClickPct', 'Rage-click sessions %', 'Repeated clicks in one spot'],
  ['uxDeadClickPct', 'Dead-click sessions %', 'Clicks that did nothing'],
  ['uxQuickbackPct', 'Quickback %', 'Left a page and came straight back'],
  ['uxScrollDepth', 'Avg scroll depth %', 'How far down pages people scroll'],
  ['uxActiveTimeSec', 'Avg active time', 'Actively using the site'],
];
const RANGES = [7, 30, 90];

function fmt(n, money, currency) {
  if (money) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase(), maximumFractionDigits: 0 }).format(n || 0);
  }
  return (n || 0).toLocaleString();
}

const isNum = (n) => n != null && n !== '' && Number.isFinite(Number(n));
const round1 = (n) => Math.round(n * 10) / 10;
const pct = (n) => (isNum(n) ? `${round1(Number(n)).toLocaleString()}%` : '–');
const count = (n) => (isNum(n) ? fmt(Math.round(Number(n))) : '–');
const shortUrl = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/\/$/, '') || '/';

function duration(n) {
  if (!isNum(n)) return '–';
  const s = Math.round(Number(n));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

// One METRICS value; `avg` values are null on days without data and show as –.
function fmtMetric(n, m, currency) {
  if (!m.avg) return fmt(n, m.money, currency);
  if (m.unit === 'pct') return pct(n);
  if (m.unit === 'time') return duration(n);
  if (m.unit === 'position') return isNum(n) ? Number(n).toFixed(1) : '–';
  return count(n);
}

function Tile({ label, value, hint }) {
  return (
    <div className="glass-panel p-4 rounded-2xl border-white/[0.05]">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{label}</span>
      <span className="text-2xl font-black text-white tracking-tight block mt-2">{value}</span>
      {hint && <span className="text-[10px] text-slate-500 block mt-1">{hint}</span>}
    </div>
  );
}

// Search Console top queries / pages, in the same compact style as "Where website visitors came from".
function SearchTable({ title, label, rows, nameKey, url }) {
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-black text-white mb-2">{title}</h4>
      {rows?.length > 0 ? (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-400">
              <th className="text-left py-1">{label}</th>
              <th className="text-right py-1 pl-3">Clicks</th>
              <th className="text-right py-1 pl-3">Impressions</th>
              <th className="text-right py-1 pl-3">CTR</th>
              <th className="text-right py-1 pl-3">Position</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r[nameKey]}-${i}`} className="border-t border-white/[0.03]">
                <td className="py-1.5 text-slate-200 w-full max-w-0 truncate" title={r[nameKey]}>{url ? shortUrl(r[nameKey]) : r[nameKey]}</td>
                <td className="py-1.5 pl-3 text-right font-mono">{fmt(r.clicks)}</td>
                <td className="py-1.5 pl-3 text-right font-mono">{fmt(r.impressions)}</td>
                <td className="py-1.5 pl-3 text-right font-mono">{r.impressions > 0 ? pct((r.clicks / r.impressions) * 100) : '–'}</td>
                <td className="py-1.5 pl-3 text-right font-mono">{isNum(r.position) ? Number(r.position).toFixed(1) : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-[10px] text-slate-500">No data yet.</p>
      )}
    </div>
  );
}

export default function GrowthPage() {
  const { API_BASE, apiFetch } = useAdmin();
  const queryClient = useQueryClient();
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState('installs');
  const [refreshing, setRefreshing] = useState(false);

  const query = useQuery({
    queryKey: ['admin-funnel', days],
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/funnel?days=${days}`);
      if (!res || !res.ok) throw new Error('Failed to load growth data');
      return res.json();
    },
  });
  const data = query.data;
  const currency = data?.current?.currency;

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await apiFetch(`${API_BASE}/funnel?days=${days}&refresh=1`);
      if (res?.ok) queryClient.setQueryData(['admin-funnel', days], await res.json());
    } finally {
      setRefreshing(false);
    }
  };

  // Search and UX columns stay hidden until their source has data (older API responses have none).
  const hasData = (key) => data?.totals?.[key] > 0 || (data?.daily || []).some((d) => d?.[key] > 0);
  const shown = METRICS.filter((m) => !(m.hideIfZero || m.avg) || hasData(m.key));
  const selected = shown.find((m) => m.key === metric) || METRICS.find((m) => m.key === 'installs');
  const points = useMemo(
    () =>
      (data?.daily || [])
        // Averages have no value on days without data; plotting those as 0 would read as a real drop.
        .filter((d) => !selected.avg || isNum(d?.[selected.key]))
        .map((d) => {
          const v = Number(d[selected.key]) || 0;
          const rounded = selected.unit === 'pct' || selected.unit === 'position' ? round1(v) : Math.round(v);
          return { ts: `${d.date}T12:00:00Z`, value: selected.avg ? rounded : v };
        }),
    [data, selected],
  );
  // Search steps only when Search Console has data; otherwise the funnel starts at visitors, as before.
  const hasSearch = SEARCH_STEPS.some((k) => data?.totals?.[k] > 0);
  const funnel = hasSearch ? FUNNEL : FUNNEL.filter((k) => !SEARCH_STEPS.includes(k));
  const funnelMax = Math.max(1, ...funnel.map((k) => data?.totals?.[k] || 0));
  const searchImpressions = data?.totals?.searchImpressions || 0;
  const showSearch = !!(data?.connected?.searchConsole || hasSearch || data?.search?.queries?.length || data?.search?.pages?.length);
  const uxDays = (data?.daily || []).filter((d) => UX_TILES.some(([k]) => isNum(d?.[k]))).length;
  const showClarity = !!(data?.connected?.clarity || data?.clarity?.latestDay || data?.clarity?.pages?.length || uxDays);
  const clarityUrl = /^https:\/\//.test(data?.clarity?.projectUrl || '') ? data.clarity.projectUrl : null;
  // Over 100% means the step has other entry points (e.g. installs straight from top.gg), so a % would mislead.
  const rate = (a, b) => (b > 0 && a <= b ? `${Math.round((a / b) * 1000) / 10}%` : '–');

  return (
    <div className="min-h-screen bg-[#070a13] text-slate-200 flex flex-col font-sans">
      <AdminNav />

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight">Growth funnel</h1>
            <p className="text-xs text-slate-400 mt-1">
              Daily journey: visit → Add to Discord → install (Free plan starts) → first translation → checkout → paid. Days are UTC.
              {data && ` Updated ${new Date(data.generatedAt).toLocaleTimeString()}.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-slate-950/50 p-1 rounded-lg border border-white/5">
              {RANGES.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`text-[10px] font-mono px-2.5 py-1 rounded-md transition-colors ${days === d ? 'bg-indigo-500/20 text-indigo-300 font-bold' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  {d}D
                </button>
              ))}
            </div>
            <button
              onClick={refresh}
              disabled={refreshing}
              title="Reload from Stripe, Google Analytics and Search Console"
              className="p-2 rounded-lg bg-slate-950/50 border border-white/5 text-slate-400 hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {query.isError && <p className="text-sm text-rose-300">Could not load growth data. Is the API up?</p>}
        {query.isLoading && <div className="w-full h-64 bg-white/5 rounded-2xl animate-pulse" />}

        {data?.warnings?.length > 0 && (
          <div className="glass-panel rounded-2xl p-4 border border-amber-500/20 space-y-1">
            {data.warnings.map((w) => (
              <p key={w} className="text-xs text-amber-200 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {w}
              </p>
            ))}
          </div>
        )}

        {data && (
          <>
            {/* Right now (Stripe snapshot) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Tile label="MRR" value={data.current ? fmt(data.current.mrr, true, currency) : '–'} hint="Active paid subscriptions, monthly" />
              <Tile label="Paying servers" value={data.current ? fmt(data.current.activePaid) : '–'} hint={data.current ? `${data.current.scheduledCancels} set to cancel` : 'Stripe not connected'} />
              <Tile label={`Free → Paid, ${days}d`} value={rate(data.totals.newPaid, data.totals.installs)} hint={`${fmt(data.totals.newPaid)} paid of ${fmt(data.totals.installs)} installs`} />
              <Tile label={`Revenue, ${days}d`} value={fmt(data.totals.revenue, true, currency)} hint={`${fmt(data.totals.churned)} churned · ${fmt(data.totals.winBacks)} won back`} />
            </div>

            {/* Funnel for the period */}
            <div className="glass-panel rounded-2xl p-5 border border-white/5">
              <h3 className="text-sm font-black text-white">Funnel, last {days} days</h3>
              <p className="text-[10px] text-slate-500 mt-0.5 mb-4">
                Each step's % is against the step above it. Installs also come from top.gg, the Discord App Directory and invite links shared inside Discord, so they can be higher than site clicks (shown as –).
                {hasSearch && ' Visitors also come from outside Google, so they are usually more than Google clicks (–). Search Console data is 2–3 days behind.'}
              </p>
              <div className="space-y-2.5">
                {funnel.map((k, i) => {
                  const m = METRICS.find((x) => x.key === k);
                  const v = data.totals?.[k] || 0;
                  return (
                    <div key={k} className="grid grid-cols-[9rem_1fr_7rem] items-center gap-3">
                      <span className="text-xs text-slate-300">{m.label}</span>
                      <div className="h-5 bg-white/[0.03] rounded">
                        <div className="h-5 bg-indigo-500/70 rounded" style={{ width: `${Math.max(v > 0 ? 1 : 0, (v / funnelMax) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-right font-mono text-slate-200">
                        {fmt(v)}
                        <span className="text-slate-500 ml-2">{i > 0 ? rate(v, data.totals?.[funnel[i - 1]] || 0) : ''}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* One metric over time */}
            <div className="glass-panel rounded-2xl p-5 border border-white/5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-sm font-black text-white">{selected.label} per day</h3>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {selected.source}
                    {selected.avg && ' · days without data are left out'}
                  </p>
                </div>
                <select
                  value={selected.key}
                  onChange={(e) => setMetric(e.target.value)}
                  className="bg-slate-950/60 border border-white/10 rounded-lg text-xs px-2 py-1.5 text-slate-200"
                >
                  {shown.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
              </div>
              <LineChart points={points} period={`${days}d`} />
            </div>

            {/* Every number, every day */}
            <div className="glass-panel rounded-2xl border border-white/5 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-white/5">
                    <th className="text-left font-semibold px-4 py-3 sticky left-0 bg-[#0b0f19]">Day</th>
                    {shown.map((m) => (
                      <th key={m.key} title={m.source} className="text-right font-semibold px-3 py-3 whitespace-nowrap">{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...data.daily].reverse().map((d) => (
                    <tr key={d.date} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="px-4 py-2 font-mono text-slate-300 sticky left-0 bg-[#0b0f19] whitespace-nowrap">{d.date}</td>
                      {shown.map((m) => (
                        <td key={m.key} className={`px-3 py-2 text-right font-mono ${d[m.key] ? 'text-slate-100' : 'text-slate-600'}`}>
                          {fmtMetric(d[m.key], m, currency)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="text-slate-200 font-bold">
                    <td className="px-4 py-3 sticky left-0 bg-[#0b0f19]">Total</td>
                    {shown.map((m) => (
                      <td
                        key={m.key}
                        title={m.avg ? (m.unit === 'position' ? 'Weighted by impressions' : 'Average of the days with data') : undefined}
                        className="px-3 py-3 text-right font-mono"
                      >
                        {fmtMetric(data.totals?.[m.key], m, currency)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
              {shown.some((m) => m.key.startsWith('search') || m.avg) && (
                <p className="text-[10px] text-slate-500 px-4 py-3 border-t border-white/5">
                  Total row: counts are summed, Google avg position is weighted by impressions, and Clarity columns are averages of the days with data (– = no data that day).
                  Search Console data is 2–3 days behind; Clarity history starts the first full day after it was switched on.
                </p>
              )}
            </div>

            {showSearch && (
              <div className="glass-panel rounded-2xl p-5 border border-white/5">
                <h3 className="text-sm font-black text-white">Google Search, last {days} days</h3>
                <p className="text-[10px] text-slate-500 mt-0.5 mb-4">
                  How often the site showed up in Google results and got clicked. Search Console data is 2–3 days behind, so the latest days look low.
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Tile label="Impressions" value={fmt(searchImpressions)} hint="Times the site showed in Google" />
                  <Tile label="Clicks" value={fmt(data.totals?.searchClicks)} hint="Clicks from Google to the site" />
                  <Tile label="CTR" value={searchImpressions > 0 ? pct(((data.totals?.searchClicks || 0) / searchImpressions) * 100) : '–'} hint="Clicks ÷ impressions" />
                  <Tile label="Avg position" value={fmtMetric(data.totals?.searchPosition, { avg: true, unit: 'position' })} hint="Weighted by impressions, 1 = top result" />
                </div>
                <div className="grid lg:grid-cols-2 gap-6 mt-5">
                  <SearchTable title="Top searches" label="Search" rows={data.search?.queries} nameKey="query" />
                  <SearchTable title="Top landing pages" label="Page" rows={data.search?.pages} nameKey="page" url />
                </div>
              </div>
            )}

            {data.sourceMedium?.length > 0 && (
              <div className="glass-panel rounded-2xl p-5 border border-white/5">
                <h3 className="text-sm font-black text-white mb-3">Where website visitors came from</h3>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400"><th className="text-left py-1">Source / medium</th><th className="text-right py-1">Sessions</th><th className="text-right py-1">Visitors</th></tr>
                  </thead>
                  <tbody>
                    {data.sourceMedium.map((s) => (
                      <tr key={`${s.source}/${s.medium}`} className="border-t border-white/[0.03]">
                        <td className="py-1.5 text-slate-200">{s.source} <span className="text-slate-500">/ {s.medium}</span></td>
                        <td className="py-1.5 text-right font-mono">{fmt(s.sessions)}</td>
                        <td className="py-1.5 text-right font-mono">{fmt(s.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.sources.length > 0 && (
              <div className="glass-panel rounded-2xl p-5 border border-white/5">
                <h3 className="text-sm font-black text-white mb-3">By channel group</h3>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400"><th className="text-left py-1">Channel</th><th className="text-right py-1">Sessions</th><th className="text-right py-1">Visitors</th></tr>
                  </thead>
                  <tbody>
                    {data.sources.map((s) => (
                      <tr key={s.channel} className="border-t border-white/[0.03]">
                        <td className="py-1.5 text-slate-200">{s.channel}</td>
                        <td className="py-1.5 text-right font-mono">{fmt(s.sessions)}</td>
                        <td className="py-1.5 text-right font-mono">{fmt(s.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showClarity && (
              <div className="glass-panel rounded-2xl p-5 border border-white/5">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-sm font-black text-white">UX quality (Microsoft Clarity), last {days} days</h3>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {uxDays > 0
                        ? `Averages of the ${uxDays} day${uxDays === 1 ? '' : 's'} with data. `
                        : 'No Clarity snapshot in this period yet; one is saved each day just after midnight UTC. '}
                      Clarity saves one snapshot per full day, starting the day after it was switched on, so earlier days are empty.
                    </p>
                  </div>
                  {clarityUrl && (
                    <a
                      href={clarityUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200"
                    >
                      Open Clarity <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {UX_TILES.map(([key, label, hint]) => (
                    <Tile key={key} label={label} value={fmtMetric(data.totals?.[key], METRICS.find((m) => m.key === key))} hint={hint} />
                  ))}
                </div>
                {data.clarity?.pages?.length > 0 && (
                  <div className="mt-5">
                    <h4 className="text-xs font-black text-white mb-2">
                      Worst pages{data.clarity.latestDay && `, ${data.clarity.latestDay}`}
                      <span className="text-[10px] font-normal text-slate-500 ml-2">latest snapshot, most rage + dead clicks first</span>
                    </h4>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-400">
                          <th className="text-left py-1">Page</th>
                          <th className="text-right py-1 pl-3">Sessions</th>
                          <th className="text-right py-1 pl-3">Rage-click %</th>
                          <th className="text-right py-1 pl-3">Dead-click %</th>
                          <th className="text-right py-1 pl-3">Quickback %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.clarity.pages.map((p, i) => (
                          <tr key={`${p.url}-${i}`} className="border-t border-white/[0.03]">
                            <td className="py-1.5 text-slate-200 w-full max-w-0 truncate" title={p.url}>{shortUrl(p.url)}</td>
                            <td className="py-1.5 pl-3 text-right font-mono">{count(p.sessions)}</td>
                            <td className="py-1.5 pl-3 text-right font-mono">{pct(p.rageClickSessionsPct)}</td>
                            <td className="py-1.5 pl-3 text-right font-mono">{pct(p.deadClickSessionsPct)}</td>
                            <td className="py-1.5 pl-3 text-right font-mono">{pct(p.quickbackSessionsPct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
